import type { FC } from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";
import { useUpdateProductPurchaseMutation } from "@/admin/entities/productPurchases";

interface RenewSubscriptionChipProps {
  id: string;
  renewSubscription: boolean;
  /** У истёкшей покупки продлевать нечего: чип остаётся справочным. */
  expired: boolean;
  /**
   * Можно ли переключать автопродление из админки: у push-провайдера
   * продление живёт на его стороне, локальный флаг только врал бы.
   */
  autoRenewEditable: boolean;
  /**
   * Подписка просрочена (PAST_DUE): срок покупки уже прошёл, но идут
   * повторы списания. Выключение их прекращает, подписка сразу истекает.
   */
  pastDue?: boolean;
}

const PROVIDER_MANAGED_HINT =
  "Автопродление управляется на стороне платёжного провайдера";

const PAST_DUE_STOP_HINT =
  "Продление не прошло, идут повторы списания. Крестик прекращает повторы: подписка сразу истечёт";

/**
 * Автопродление подписки покупки: чип-переключатель, как в легаси
 * (CancelRenewSubscription). Выключенное продление включается обратно
 * иконкой обновления.
 */
export const RenewSubscriptionChip: FC<RenewSubscriptionChipProps> = ({
  id,
  renewSubscription,
  expired,
  autoRenewEditable,
  pastDue = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateProductPurchase, { loading }] = useUpdateProductPurchaseMutation(
    {
      variables: { id, input: { renewSubscription: !renewSubscription } },
      refetchQueries: ["ProductPurchasesList"],
      onCompleted: () => {
        enqueueSnackbar("Покупка обновлена", { variant: "success" });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  const label = renewSubscription ? "Включено" : "Выключено";
  const color = renewSubscription ? "primary" : "default";
  const readOnlyChip = <Chip size="small" color={color} label={label} />;

  if (pastDue && renewSubscription) {
    const pastDueChip = (
      <Chip size="small" color="warning" label="Просрочена" />
    );
    if (!autoRenewEditable) {
      return <Tooltip title={PROVIDER_MANAGED_HINT}>{pastDueChip}</Tooltip>;
    }
    return (
      <Tooltip title={PAST_DUE_STOP_HINT}>
        <Chip
          size="small"
          color="warning"
          label="Просрочена"
          disabled={loading}
          onDelete={(event) => {
            event.stopPropagation();
            void updateProductPurchase();
          }}
        />
      </Tooltip>
    );
  }

  if (expired) {
    return readOnlyChip;
  }

  if (!autoRenewEditable) {
    return <Tooltip title={PROVIDER_MANAGED_HINT}>{readOnlyChip}</Tooltip>;
  }

  return (
    <Chip
      size="small"
      color={color}
      label={label}
      disabled={loading}
      deleteIcon={renewSubscription ? undefined : <RefreshIcon />}
      onDelete={(event) => {
        event.stopPropagation();
        void updateProductPurchase();
      }}
    />
  );
};
